class FeeDollarNotional {
  static loggedScenarios = new Set()

  static logOnce(key, message) {
    if (!this.loggedScenarios.has(key)) {
      this.loggedScenarios.add(key)
      console.log(message)
    }
  }

  static calculateDollarNotional(trade, priceRates) {
    const logKey = `${trade.id}`
    let dollar_notional = 0
    const base_currency = trade.base_currency
    const quantityField = trade.direction === 'Client' ? 'client_quantity' : 'market_maker_quantity'
    const quantity = parseFloat(trade[quantityField])
    // Skip if this side doesn't have data - all will have an empty side
    if (!quantity || !base_currency) {
      return null
    }
    //const quantity = trade.quantity
    this.logOnce(
      logKey + '_0',
      `Calculating Dollar Notional for trade ${trade.id} with base currency ${base_currency} and quantity ${quantity}`
    )
    //console.log(trade)
    // Case 1: Premium already in USD, no conversion needed
    if (base_currency === 'USD') {
      this.logOnce(
        logKey + '_1',
        `Notional for trade ${trade.id} is already in USD, using quantity: ${quantity}`
      )
      dollar_notional = quantity
    } else {
      // Case 2: Attempt to convert to USD using current snapshot
      const usdToBaseCurrency = `USD${base_currency}`.toUpperCase()
      const baseCurrencyToUSD = `${base_currency}USD`.toUpperCase()

      if (!priceRates[usdToBaseCurrency] && !priceRates[baseCurrencyToUSD]) {
        this.logOnce(
          logKey + '_1',
          `FX rate for ${base_currency}/USD not found; skipping trade ${trade.id}.`
        )
        return null
      }

      // Case 2a: Direct premium/USD rate available (e.g., EURUSD = 1.10)
      if (priceRates[baseCurrencyToUSD]) {
        const converted = priceRates[baseCurrencyToUSD].LAST_PRICE * quantity
        dollar_notional = converted
        this.logOnce(
          logKey + '_1',
          `Converted dollar notional via ${base_currency}/USD: ${quantity} * ${priceRates[baseCurrencyToUSD].LAST_PRICE} = ${converted}`
        )
        // Case 2b: Inverted USD/premium rate available (e.g., USDEUR = 0.91 → invert)
      } else if (priceRates[usdToBaseCurrency]) {
        const converted = (1 / priceRates[usdToBaseCurrency].LAST_PRICE) * quantity
        dollar_notional = converted
        this.logOnce(
          logKey + '_1',
          `Converted dollar notional via USD/${base_currency}: ${quantity} * (1 / ${priceRates[usdToBaseCurrency].LAST_PRICE}) = ${converted}`
        )
      }
    }
    return dollar_notional
  }

  static calculateDollarNotionalSwap(trade, priceRates) {
    //console.log('Calculating Dollar Notional for Swap trade:', trade.id)
    const logKey = `${trade.id}`
    let dollar_notional = 0
    let dollar_notional_far = 0
    const base_currency = trade.base_currency
    const quantityField = trade.direction === 'Client' ? 'client_quantity' : 'market_maker_quantity'
    const farQuantityField = 'far_leg_quantity'
    const quantity = parseFloat(trade[quantityField])
    const farQuantity = parseFloat(trade[farQuantityField])
    // Skip if this side doesn't have data - all will have an empty side
    if (!quantity || !base_currency || !farQuantity) {
      return null
    }
    //const quantity = trade.quantity
    this.logOnce(
      logKey + '_0',
      `Calculating Dollar Notional for trade ${trade.id} with base currency ${base_currency}, quantity ${quantity} and far leg quantity ${farQuantity}`
    )
    //console.log(trade)
    // Case 1: Premium already in USD, no conversion needed
    if (base_currency === 'USD') {
      this.logOnce(
        logKey + '_1',
        `Notional for trade ${trade.id} is already in USD, using quantity: ${quantity} and far leg quantity: ${farQuantity}`
      )
      dollar_notional = quantity
      dollar_notional_far = farQuantity
    } else {
      // Case 2: Attempt to convert to USD using current snapshot
      const usdToBaseCurrency = `USD${base_currency}`.toUpperCase()
      const baseCurrencyToUSD = `${base_currency}USD`.toUpperCase()

      if (!priceRates[usdToBaseCurrency] && !priceRates[baseCurrencyToUSD]) {
        this.logOnce(
          logKey + '_1',
          `FX rate for ${base_currency}/USD not found; skipping trade ${trade.id}.`
        )
        return null
      }

      // Case 2a: Direct premium/USD rate available (e.g., EURUSD = 1.10)
      if (priceRates[baseCurrencyToUSD]) {
        const converted = priceRates[baseCurrencyToUSD].LAST_PRICE * quantity
        const convertedFar = priceRates[baseCurrencyToUSD].LAST_PRICE * farQuantity
        dollar_notional = converted
        dollar_notional_far = convertedFar
        this.logOnce(
          logKey + '_1',
          `Converted dollar notional via ${base_currency}/USD: ${quantity} * ${priceRates[baseCurrencyToUSD].LAST_PRICE} = ${converted} and far leg ${farQuantity} * ${priceRates[baseCurrencyToUSD].LAST_PRICE} = ${convertedFar}`
        )
        // Case 2b: Inverted USD/premium rate available (e.g., USDEUR = 0.91 → invert)
      } else if (priceRates[usdToBaseCurrency]) {
        const converted = (1 / priceRates[usdToBaseCurrency].LAST_PRICE) * quantity
        const convertedFar = (1 / priceRates[usdToBaseCurrency].LAST_PRICE) * farQuantity
        dollar_notional = converted
        dollar_notional_far = convertedFar
        this.logOnce(
          logKey + '_1',
          `Converted dollar notional via USD/${base_currency}: ${quantity} * (1 / ${priceRates[usdToBaseCurrency].LAST_PRICE}) = ${converted} and far leg ${farQuantity} * (1 / ${priceRates[usdToBaseCurrency].LAST_PRICE}) = ${convertedFar}`
        )
      }
    }
    return (Math.abs(dollar_notional) + Math.abs(dollar_notional_far)) / 2
  }

  static calculateFee(trade) {
    const logKey = `${trade.id}_fee`
    const primeBroker = trade.makor_prime_broker
    const preciousMetals = ['XAU', 'XAG', 'XPD', 'XPT']
    const isPreciousMetal =
      preciousMetals.includes(trade.base_currency) ||
      preciousMetals.includes(trade.counter_currency)
    const dollarNotional = trade.dollar_notional
    let fee = 0
    if (!primeBroker || !dollarNotional) {
      this.logOnce(
        logKey + '_0',
        `Either no prime broker or no dollar notional found for trade ${trade.id}, cannot calculate fee.`
      )
      return null
    }
    const bridgeAccounts = [
      144, // Makor
      98 // OG
    ]
    if (bridgeAccounts.includes(trade.entity_id)) {
      this.logOnce(logKey + '_0', `Bridge account detected for trade ${trade.id}, no fee applied.`)
      return 0
    }
    if (primeBroker === 'HSBC') {
      this.logOnce(logKey + '_0', `Using HSBC Fee Calculation logic for trade ${trade.id}.`)
      if (trade.entity_id === 73) {
        // HSBC entity
        this.logOnce(logKey + '_1', `HSBC entity, no fee applied.`)
        fee = 0
      }
      const cashTradeTypes = ['Spot', 'Forward', 'Swap', 'NDF']
      // Case 1: Cash trade types
      if (cashTradeTypes.some(k => trade.type.includes(k))) {
        this.logOnce(logKey + '_1', `Calculating fee for cash trade type: ${trade.type}`)
        if (isPreciousMetal) {
          fee = parseFloat((dollarNotional / 1000000) * 5)
        } else {
          fee = parseFloat((dollarNotional / 1000000) * 3.5)
        }
        // Case 2: If it is an option
      } else if (trade.type.includes('Option')) {
        if (isPreciousMetal) {
          this.logOnce(
            logKey + '_1',
            `Calculating fee for Precious Metal Option trade type: ${trade.type}`
          )
          fee = parseFloat((dollarNotional / 1000000) * 7)
        }
        if (trade.type.includes('Digital')) {
          this.logOnce(
            logKey + '_1',
            `Calculating fee for Digital Option trade type: ${trade.type}`
          )
          fee = 300
        } else {
          this.logOnce(logKey + '_1', `Calculating fee for Option trade type: ${trade.type}`)
          fee = parseFloat((dollarNotional / 1000000) * 5)
        }
      }
    } else if (primeBroker === 'RBS') {
      this.logOnce(logKey + '_0', `Using RBS Fee Calculation logic for trade ${trade.id}.`)
      if (trade.entity_id === 159) {
        // RBS entity
        this.logOnce(logKey + '_1', `RBS entity, no fee applied.`)
        return 0
      }
      const barrierOptionTypes = ['KO', 'KI', 'RKO', 'RKI']
      if (trade.type.includes('Spot')) {
        this.logOnce(logKey + '_1', `Calculating fee for Spot trade type: ${trade.type}. $2.25/1m.`)
        fee = parseFloat((dollarNotional / 1000000) * 2.25)
        const x2MultiplerEntites = [
          48, // Bankinter
          117, // Standard Bank of SA
          77, // Investec
          95, // Nedbank
          // We don't have Mediobanca
          31, // ABSA
          // We don't have Rabobank
          // We don't have SMBC
          193 // Santander Bank Mexico
        ]
        if (x2MultiplerEntites.includes(trade.entity_id)) {
          this.logOnce(logKey + '_2', `Applying x2 multiplier for entity ${trade.entity_id}.`)
          fee = parseFloat(fee * 2)
        }
        const voiceBrokerEntities = [
          187, // BGC
          192 // TPAC
        ]
        if (voiceBrokerEntities.includes(trade.entity_id)) {
          const additionalFee = parseFloat((dollarNotional / 1000000) * 1)
          this.logOnce(
            logKey + '_2',
            `Applying additional voice broker fee: ${additionalFee}. $1/1m. (This is for BGC and TPAC entities)`
          )
          fee += additionalFee
        }
      } else if (trade.type.includes('Forward') || trade.type.includes('NDF')) {
        this.logOnce(
          logKey + '_1',
          `Calculating fee for Forward/NDF trade type: ${trade.type}. $6/1m.`
        )
        fee = parseFloat((dollarNotional / 1000000) * 6)
        const x2MultiplerEntites = [
          48, // Bankinter
          117, // Standard Bank of SA
          77, // Investec
          95 // Nedbank
        ]
        if (x2MultiplerEntites.includes(trade.entity_id)) {
          this.logOnce(logKey + '_2', `Applying x2 multiplier for entity ${trade.entity_id}.`)
          fee = parseFloat(fee * 2)
        }
      } else if (trade.type.includes('Digital')) {
        this.logOnce(
          logKey + '_1',
          `Calculating fee for Digital Option trade type: ${trade.type}. $13/1m, min $75.`
        )
        fee = parseFloat((dollarNotional / 1000000) * 13)
        const x2MultiplerEntites = [
          48, // Bankinter
          117, // Standard Bank of SA
          95 // Nedbank
        ]
        if (x2MultiplerEntites.includes(trade.entity_id)) {
          this.logOnce(logKey + '_2', `Applying x2 multiplier for entity ${trade.entity_id}.`)
          fee = parseFloat(fee * 2)
        }
        fee = fee < 75 ? 75 : fee
      } else if (barrierOptionTypes.some(k => trade.type.includes(k))) {
        this.logOnce(
          logKey + '_1',
          `Calculating fee for Barrier Option trade type: ${trade.type}. $7.5/1m, min $75.`
        )
        fee = parseFloat((dollarNotional / 1000000) * 7.5)
        const x2MultiplerEntites = [
          48, // Bankinter
          117, // Standard Bank of SA
          95 // Nedbank
        ]
        if (x2MultiplerEntites.includes(trade.entity_id)) {
          this.logOnce(logKey + '_2', `Applying x2 multiplier for entity ${trade.entity_id}.`)
          fee = parseFloat(fee * 2)
        }
        fee = fee < 75 ? 75 : fee
      } else if (trade.type.includes('Option')) {
        this.logOnce(logKey + '_1', `Calculating fee for Option trade type: ${trade.type}. $7/1m.`)
        fee = parseFloat((dollarNotional / 1000000) * 7)
        const x2MultiplerEntites = [
          48, // Bankinter
          117, // Standard Bank of SA
          95 // Nedbank
        ]
        if (x2MultiplerEntites.includes(trade.entity_id)) {
          this.logOnce(logKey + '_2', `Applying x2 multiplier for entity ${trade.entity_id}.`)
          fee = parseFloat(fee * 2)
        }
      } else if (trade.type.includes('Swap')) {
        this.logOnce(
          logKey + '_1',
          `Calculating fee for Swap trade type: ${trade.type}. Fee depends on value dates.`
        )
        const near_value_date = trade.value_date
        const far_value_date = trade.far_value_date
        const trade_date = trade.trade_date
        const tradeDate = new Date(trade_date)
        const nearValue = new Date(near_value_date)
        const farValue = new Date(far_value_date)
        const nearDiff = nearValue - tradeDate
        const farDiff = farValue - tradeDate
        const nearDiffDays = nearDiff / (1000 * 60 * 60 * 24)
        const farDiffDays = farDiff / (1000 * 60 * 60 * 24)
        this.logOnce(
          logKey + '_2',
          `Swap near value date difference: ${nearDiffDays} days. Far value date difference: ${farDiffDays} days.`
        )

        if (nearDiffDays <= 5 && farDiffDays <= 5) {
          this.logOnce(
            logKey + '_3',
            `Near and Far value dates are within 5 days, applying Spot FX fee ($2.25 per 1m).`
          )
          fee = parseFloat((dollarNotional / 1000000) * 2.25) // We are only doing this for far leg, so only charge fee once.
          // We need more granular control here if we want to be very specific about the far leg fee (i.e., if far leg notional is different to near leg)
        } else if (nearDiffDays > 5 && farDiffDays > 5) {
          this.logOnce(
            logKey + '_3',
            `Both near and far value date is more than 5 days away, applying Forward FX fee ($6 per 1m) to both legs.`
          )
          fee = parseFloat((dollarNotional / 1000000) * 6 * 2)
        } else if (farDiffDays > 5) {
          this.logOnce(
            logKey + '_3',
            `Only far value date is more than 5 days away, applying Forward FX fee ($6 per 1m) to far leg..`
          )
          fee = parseFloat((dollarNotional / 1000000) * 6)
        }

        const x2MultiplerEntites = [
          48, // Bankinter
          117 // Standard Bank of SA
        ]
        if (x2MultiplerEntites.includes(trade.entity_id)) {
          this.logOnce(logKey + '_2', `Applying x2 multiplier for entity ${trade.entity_id}.`)
          fee = parseFloat(fee * 2)
        }
      }
    } else if (primeBroker === 'SCB') {
      this.logOnce(logKey + '_0', `Using SCB Fee Calculation logic for trade ${trade.id}.`)
      if (trade.entity_id === 108) {
        // SCB entity
        this.logOnce(logKey + '_1', `SCB entity, no fee applied.`)
        return 0
      }
      if (isPreciousMetal) {
        this.logOnce(
          logKey + '_1',
          `Calculating fee for Precious Metal trade type: ${trade.type}. $3/1m.`
        )
        fee = parseFloat((dollarNotional / 1000000) * 3)
        fee = fee < 1.5 ? 1.5 : fee // Minimum fee of $1.50
      } else if (trade.type.includes('Spot')) {
        this.logOnce(logKey + '_1', `Calculating fee for Spot trade type: ${trade.type}. $2/1m.`)
        fee = parseFloat((dollarNotional / 1000000) * 2)
        fee = fee < 1 ? 1 : fee // Minimum fee of $1
      } else if (trade.type.includes('Forward') || trade.type.includes('NDF')) {
        this.logOnce(
          logKey + '_1',
          `Calculating fee for Forward/NDF trade type: ${trade.type}. $3.5/1m.`
        )
        fee = parseFloat((dollarNotional / 1000000) * 3.5)
      } else if (trade.type.includes('Option')) {
        this.logOnce(logKey + '_1', `Calculating fee for Option trade type: ${trade.type}. $5/1m.`)
        fee = parseFloat((dollarNotional / 1000000) * 5)
      }
    } else {
      this.logOnce(
        logKey + '_0',
        `No valid prime broker found for trade ${trade.id}, cannot calculate fee.`
      )
      fee = null
    }
    this.logOnce(
      logKey + '_4',
      `Final fee for trade ${trade.id} is: ${fee} (based on dollar notional: ${dollarNotional})`
    )
    return fee
  }
}
export default FeeDollarNotional
